<template>
  <div class="card-wrapper">
    <b-card v-for="order in orders" :key="order.id" class="card-text mb-4">
      <div class="d-md-flex justify-content-between align-items-start">
        <div class="d-block flex-grow-1 mb-md-0 mb-4 mr-md-3 mr-0">
          <div class="w-100 mb-2">
            <p class="text-muted mb-0">{{ $t('general.paymentAmount') }}</p>
            <div>
              {{ formatPrice(Number(order.amount), 2, 3, ' ', '.') }}
              {{ paymentCurrency.name }}
            </div>
          </div>

          <div class="w-100 mb-2">
            <p class="text-muted mb-0">{{ $t('general.paymentMethod') }}</p>
            <div>{{ order.payment.name }}</div>
          </div>

          <div class="w-100 mb-2">
            <p class="text-muted mb-0">{{ $t('anyform.fullname') }}</p>
            <div>{{ order.address != null ? order.address['full_name'] : order.customer_name }}</div>
          </div>

          <div class="w-100 mb-2">
            <p class="text-muted mb-0">{{ $t('anyform.phone') }}</p>
            <div>{{ order.address != null ? order.address['phone'] : order.phone }}</div>
          </div>

          <!-- <div class="w-100 mb-2">
            <p class="text-muted mb-0">{{ $t('anyform.email') }}</p>
            <div>{{ order.address != null ? order.address['email'] : order.email }} </div>
          </div> -->

          <div class="w-100 mb-2" v-if="order.address != null">
            <p class="text-muted mb-0">{{ $t('anyform.address') }}</p>
            <div>
              {{ order.address != null ? order.address.district.name_ru + ' ' + order.address.street : order.address }}
            </div>
          </div>

          <div v-if="order.message" class="w-100">
            <p class="text-muted mb-0">{{ $t('anyform.message') }}</p>
            <div class="pr-5">{{ order.address != null ? order.address.note : order.message }}</div>
          </div>
        </div>

        <div class="actions d-flex justify-content-end mt-xs-0 mt-4">
          <i
            class="glyph-icon datalist-icon mr-2"
            :class="`status--${order.status} ${statusList[order.status].icon}`"
          ></i>
          <i
            class="glyph-icon simple-icon-pencil datalist-icon mr-2"
            title="Change status"
            @click="showInfoModal({ id: order.id, status: order.status }, order.products)"
          ></i>
          <i class="glyph-icon simple-icon-trash datalist-icon" @click="handleDelete(order.id)"></i>
        </div>
      </div>
    </b-card>

    <!-- Modals -->
    <b-modal
      :id="`editSingleOrder${status}Modal`"
      :ref="`editSingleOrder${status}Modal`"
      size="xl"
      :title="'Edit Status'"
    >
      <b-row v-if="!isLoading">
        <b-colxx xxs="12">
          <div class="my-table-responsive">
            <table class="my-table">
              <thead>
                <tr>
                  <th scope="col" style="min-width: 120px">
                    {{ $t('pages.orders.orderDate') }}
                  </th>
                  <th scope="col" style="min-width: 150px">
                    {{ $t('pages.orders.productName') }}
                  </th>
                  <th scope="col" style="min-width: 120px">Barcode</th>
                  <th scope="col" style="min-width: 60px">
                    {{ $t('pages.orders.quantity') }}
                  </th>
                  <th scope="col" style="min-width: 60px">
                    {{ $t('pages.products.unit') }}
                  </th>
                  <th scope="col" style="min-width: 100px">
                    {{ $t('pages.orders.price') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(product, idx) in currentProducts" :key="product.id">
                  <td v-if="idx === 0" :rowspan="currentProducts.length">
                    {{ getDateWithFormatCustom(new Date(product.updated_at)) }}
                  </td>
                  <td>{{ product.name }}</td>
                  <td>{{ product.barcode || '' }}</td>
                  <td>
                    {{ product.pivot.count }}
                  </td>
                  <td>
                    <template v-if="product.unit !== 'no'">
                      {{ $t(`general.units.${product.unit}`) }}
                    </template>
                    <template v-else>-</template>
                  </td>
                  <td>
                    {{ formatPrice(Number(product.price), 2, 3, ' ', '.') }}
                    {{ paymentCurrency.name }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <b-form @submit.prevent="onSubmit" class="av-tooltip my-3">
            <b-form-group :label="$t('anyform.chooseStatus')">
              <v-select v-model="$v.currentStatus['status'].$model" :options="Object.keys(statusList)" />
              <div
                :class="{
                  'invalid-feedback': true,
                  'd-block': $v.currentStatus['status'].$error && !$v.currentStatus['status'].required
                }"
              >
                {{ $t('formErrors.chooseStatus') }}
              </div>
            </b-form-group>

            <b-button type="submit" variant="primary" class="mt-2">
              {{ $t('buttons.save') }}
            </b-button>
          </b-form>
        </b-colxx>
      </b-row>
      <div v-else class="d-flex justify-content-center">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
      </div>

      <template slot="modal-footer">
        <b-button variant="secondary" @click="hideModal(`editSingleOrder${status}Modal`)">
          {{ $t('buttons.cancel') }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { formatPrice, handleObjectInternalization, getDateWithFormatCustom } from '@/utils'
const { required } = require('vuelidate/lib/validators')

export default {
  props: {
    orders: {
      type: Array,
      required: true
    },
    status: {
      type: String,
      required: true
    }
  },
  validations: {
    currentStatus: {
      status: { required }
    }
  },
  data: () => ({
    isLoading: false,
    currentStatus: { id: null, status: '' },
    currentProducts: [],
    statusList: {
      waiting: { icon: 'simple-icon-clock' },
      approved: { icon: 'simple-icon-check' },
      delivery: { icon: 'simple-icon-control-forward' },
      finish: { icon: 'simple-icon-check' },
      cancel: { icon: 'simple-icon-close' }
    }
  }),
  computed: {
    paymentCurrency() {
      const paymentCurrency = this.$store.getters.paymentCurrency
      return handleObjectInternalization(paymentCurrency, ['name'], this.$i18n.locale)
    }
  },
  mounted() {
    this.$root.$on('bv::modal::show', (_bvEvent, modalId) => {
      if (modalId === `editSingleOrder${this.status}Modal`) {
        this.clearForm()
      }
    })
  },
  methods: {
    getDateWithFormatCustom,
    formatPrice,
    showInfoModal(status, currentProducts) {
      this.$bvModal.show(`editSingleOrder${this.status}Modal`)
      this.currentStatus.status = status.status
      this.currentStatus.id = status.id
      this.currentProducts = currentProducts
    },
    handleDelete(id) {
      this.$confirm({
        message: this.$t('confirm-dialog.question'),
        button: {
          no: this.$t('confirm-dialog.no'),
          yes: this.$t('confirm-dialog.yes')
        },
        callback: (confirm) => {
          if (confirm) {
            this.$store.dispatch('orders/deleteSingleOrder', {
              id,
              vm: this
            })
          }
        }
      })
    },
    onSubmit() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.$store.dispatch('orders/updateSingleOrderStatus', {
          payload: this.currentStatus,
          vm: this
        })
      }
    },
    clearForm() {
      this.currentStatus.id = null
      this.currentStatus.status = ''
      this.currentProducts = []
    },
    hideModal(refname) {
      this.$refs[refname].hide()
    }
  }
}
</script>
